/**
 * "Can't find a resident?" beside the Facility rounds search box (Isaiah Cotto, 2026-10-28; docs/provider/CE_PULL.md).
 *
 * After Jill Gentile wrote that a resident she had tested and qualified was "not in EMERGE": the provider types the
 * CounselEAR patient id (or, where switched on, the exact last name). eMerge looks in its own records first and says
 * plainly "Already in eMerge at <building>", with one click to show them here; only when eMerge holds nobody does it
 * ask CounselEAR, and then it shows the minimum to confirm the person (name, date of birth, CounselEAR id, Preferred
 * Clinic) and what adding does, before the provider adds them. Nothing is said to have happened until the server says
 * so. Shown only where the day sheet offers it (provenance.cePull); the server decides every request again.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus } from "lucide-react";
import { Button } from "../../../components/new-components/ui/button";
import { Input } from "../../../components/new-components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../components/new-components/ui/sheet";
import { Notice } from "./FieldPrimitives";
import { worklistLabel } from "./manifestLenses";
import {
  cePullApi,
  dobWords,
  newPullKey,
  type CePullAdd,
  type CePullLookup,
  type CePullOffer,
  type CePullPreview,
  type CePullResident,
  type LookupInput,
} from "./cePullApi";

type Method = "id" | "name";

const NOT_REACHED = "eMerge did not answer. Nothing was looked up or added. Check your connection and try again.";

const residentName = (r: CePullResident) =>
  r.name ? [r.name.first_name, r.name.last_name].filter(Boolean).join(" ") || "Name not recorded" : "A resident";
const previewName = (p: CePullPreview) => [p.first_name, p.last_name].filter(Boolean).join(" ") || "No name in CounselEAR";
const clinicWords = (p: CePullPreview) =>
  p.clinic ? (p.clinic.name ? `${p.clinic.name} (CounselEAR clinic ${p.clinic.id})` : `CounselEAR clinic ${p.clinic.id}`) : "None named in CounselEAR";

/** One resident eMerge holds: where, and what the provider can do about it from here. */
function ResidentAnswer({
  r,
  facilityName,
  onShowHere,
}: {
  r: CePullResident;
  facilityName: string;
  onShowHere: (r: CePullResident) => void;
}) {
  const where = r.building ? (r.building.here ? facilityName : r.building.name || "another building") : "no building yet";
  return (
    <li className="fw-cepull-resident">
      <p>
        <strong>{residentName(r)}</strong> · in eMerge at {where}
        {r.status ? ` · ${r.status}` : ""}
      </p>
      {r.building && r.building.here && r.show && r.patient_id && (
        <Button
          variant="outline"
          type="button"
          onClick={() => onShowHere(r)}
          aria-label={`Show ${residentName(r)} on ${worklistLabel(r.show.lens)}`}
        >
          Show on {worklistLabel(r.show.lens)}
        </Button>
      )}
      {r.building && r.building.here && !r.show && (
        <p className="fw-muted">Their status puts them on no worklist here. Tell the office.</p>
      )}
      {r.building && !r.building.here && r.building.covered && (
        <Link className="fw-resource" to={`/provider/${encodeURIComponent(r.building.id)}?view=manifest`}>
          Open {r.building.name || "that building"}
        </Link>
      )}
      {r.ask && <p className="fw-muted">{r.ask.words}</p>}
    </li>
  );
}

export default function CePull({
  facilityId,
  facilityName,
  offer,
  offline,
  onShow,
}: {
  facilityId: string;
  facilityName: string;
  offer: CePullOffer | undefined;
  offline: boolean;
  /** Show a resident of this building: their worklist and page, then open their workbench. */
  onShow: (patientId: string, lens: string, offset: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<Method>("id");
  const [ceId, setCeId] = useState("");
  const [last, setLast] = useState("");
  const [first, setFirst] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [found, setFound] = useState<CePullLookup | null>(null);
  const [added, setAdded] = useState<CePullAdd | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [key, setKey] = useState(newPullKey);

  if (!offer || !offer.allowed) return null;
  const byName = offer.by_name === true;

  const reset = () => {
    setProblem("");
    setFound(null);
    setAdded(null);
    setConfirmed(false);
  };

  const ask = async (input: LookupInput) => {
    if (busy || offline) return;
    setBusy(true);
    reset();
    try {
      const r = await cePullApi.lookup(facilityId, input);
      if (r.success && r.data) {
        setFound(r.data);
        setKey(newPullKey());
      } else if (r.status === undefined && !r.code) setProblem(NOT_REACHED);
      else setProblem(r.message || r.error || "eMerge could not look that up. Tell the office.");
    } catch {
      setProblem(NOT_REACHED);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (method === "id") {
      const v = ceId.trim();
      if (!/^\d{1,12}$/.test(v)) {
        setProblem("A CounselEAR patient id is the number CounselEAR shows: digits only.");
        return;
      }
      ask({ counselear_id: v });
    } else {
      if (last.trim().length < 3) {
        setProblem("Type the whole last name, at least 3 letters. For a shorter name, use the CounselEAR patient id.");
        return;
      }
      ask({ last_name: last.trim(), ...(first.trim() ? { first_name: first.trim() } : {}) });
    }
  };

  const addNow = async () => {
    const id = found && found.preview ? found.preview.counselear_id : null;
    if (!id || busy || offline || !confirmed) return;
    setBusy(true);
    setProblem("");
    try {
      const r = await cePullApi.add(facilityId, id, key);
      if (r.success && r.data) {
        setAdded(r.data);
        setFound(null);
      } else if (r.status === undefined && !r.code) {
        // Never reached, or the answer was lost: the same key, so trying again cannot add twice.
        setProblem("eMerge did not confirm the add. Nothing is shown as added. Try again; trying again will not add them twice.");
      } else {
        setProblem(r.message || r.error || "eMerge could not add them. Nothing was added. Tell the office.");
        setKey(newPullKey());
      }
    } catch {
      setProblem("eMerge did not confirm the add. Nothing is shown as added. Try again; trying again will not add them twice.");
    } finally {
      setBusy(false);
    }
  };

  const showHere = (r: CePullResident) => {
    if (!r.patient_id || !r.show) return;
    onShow(r.patient_id, r.show.lens, r.show.offset);
    setOpen(false);
  };

  const close = () => {
    if (busy) return;
    setOpen(false);
    reset();
  };

  const preview = found && found.preview;
  const addInfo = found && found.add;
  const addable = Boolean(preview && addInfo && addInfo.open && (addInfo.would === "CREATE" || addInfo.would === "LINK" || addInfo.would === "REVIEW"));

  return (
    <>
      <Button
        variant="outline"
        type="button"
        className="fw-cepull-open"
        onClick={() => setOpen(true)}
        aria-label="Can't find a resident? Look them up in eMerge and CounselEAR"
      >
        <UserPlus />
        Can&rsquo;t find a resident?
      </Button>
      <Sheet open={open} onOpenChange={(next) => { if (!next) close(); }}>
        <SheetContent className="fw-drawer" onInteractOutside={(e) => { if (busy) e.preventDefault(); }}>
          <SheetHeader>
            <span className="fw-eyebrow">Find or add a resident</span>
            <SheetTitle>Can&rsquo;t find a resident?</SheetTitle>
            <SheetDescription>
              eMerge looks in its own records first. Only if it holds nobody does it ask CounselEAR.
            </SheetDescription>
          </SheetHeader>
          <form className="fw-form" onSubmit={submit} aria-label="Look up a resident">
            {byName && (
              <fieldset className="fw-choice" disabled={busy}>
                <legend>Look up by</legend>
                <label>
                  <input type="radio" name="fw-cepull-method" checked={method === "id"} onChange={() => { setMethod("id"); reset(); }} />
                  CounselEAR patient id
                </label>
                <label>
                  <input type="radio" name="fw-cepull-method" checked={method === "name"} onChange={() => { setMethod("name"); reset(); }} />
                  Last name
                </label>
              </fieldset>
            )}
            {method === "id" ? (
              <>
                <label htmlFor="fw-cepull-id">CounselEAR patient id</label>
                <Input
                  id="fw-cepull-id"
                  inputMode="numeric"
                  autoComplete="off"
                  value={ceId}
                  maxLength={12}
                  readOnly={busy}
                  onChange={(e) => setCeId(e.target.value.replace(/\D/g, ""))}
                />
                <p className="fw-muted">The number CounselEAR shows on the patient.{byName ? "" : " Looking up by name is not switched on."}</p>
              </>
            ) : (
              <>
                <label htmlFor="fw-cepull-last">Last name (the whole name)</label>
                <Input id="fw-cepull-last" autoComplete="off" value={last} maxLength={50} readOnly={busy} onChange={(e) => setLast(e.target.value)} />
                <label htmlFor="fw-cepull-first">First name (optional)</label>
                <Input id="fw-cepull-first" autoComplete="off" value={first} maxLength={50} readOnly={busy} onChange={(e) => setFirst(e.target.value)} />
                <p className="fw-muted">eMerge searches the buildings you cover; CounselEAR is asked for an exact last name and shows at most 5 people.</p>
              </>
            )}
            <div className="fw-actions">
              <Button className="fw-primary" type="submit" disabled={busy || offline}>
                {busy && !preview ? "Looking…" : "Look up"}
              </Button>
              <Button variant="outline" type="button" disabled={busy} onClick={close}>Close</Button>
            </div>
            {offline && <p className="fw-muted">You are offline. Look up once you are connected.</p>}
          </form>

          <div className="fw-cepull-answer" aria-live="polite">
            {problem && <p role="alert" className="fw-error">{problem}</p>}
            {busy && <p role="status" className="fw-save-status">Asking eMerge…</p>}

            {found && found.outcome !== "FOUND_IN_COUNSELEAR" && (
              <Notice>{found.words}</Notice>
            )}
            {found && found.residents && found.residents.length > 0 && (
              <ul className="fw-cepull-list" aria-label="Residents eMerge holds">
                {found.residents.map((r, i) => (
                  <ResidentAnswer key={r.patient_id || `r${i}`} r={r} facilityName={facilityName} onShowHere={showHere} />
                ))}
              </ul>
            )}
            {found && found.can_ask_counselear && method === "name" && (
              <Button
                variant="outline"
                type="button"
                disabled={busy || offline}
                onClick={() => ask({ last_name: last.trim(), ...(first.trim() ? { first_name: first.trim() } : {}), ask_counselear: true })}
              >
                None of these: ask CounselEAR
              </Button>
            )}

            {found && found.candidates && found.candidates.length > 0 && (
              <>
                <p>{found.words}</p>
                <ul className="fw-cepull-list" aria-label="CounselEAR patients with this name">
                  {found.candidates.map((c) => (
                    <li key={c.counselear_id} className="fw-cepull-resident">
                      <p>
                        <strong>{previewName(c)}</strong> · born {dobWords(c.dob)} · CounselEAR {c.counselear_id} · {clinicWords(c)}
                        {c.in_emerge ? " · already in eMerge" : ""}
                      </p>
                      <Button
                        variant="outline"
                        type="button"
                        disabled={busy || offline}
                        onClick={() => { setMethod("id"); setCeId(c.counselear_id); ask({ counselear_id: c.counselear_id }); }}
                        aria-label={`Choose ${previewName(c)}, CounselEAR ${c.counselear_id}`}
                      >
                        Choose
                      </Button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {preview && found && found.outcome === "FOUND_IN_COUNSELEAR" && (
              <section className="fw-cepull-preview" aria-label="What CounselEAR holds">
                <p>{found.words}</p>
                <dl className="fw-facts">
                  <dt>Name</dt><dd>{previewName(preview)}</dd>
                  <dt>Date of birth</dt><dd>{dobWords(preview.dob)}</dd>
                  <dt>CounselEAR id</dt><dd>{preview.counselear_id}</dd>
                  <dt>Preferred Clinic</dt><dd>{clinicWords(preview)}</dd>
                </dl>
                {addInfo && <Notice>{addInfo.words}</Notice>}
                {addInfo && addInfo.twl_q && (
                  <p className="fw-muted">After you add them, TWL Q from their workbench: {addInfo.twl_q.words}</p>
                )}
                {addInfo && !addInfo.open && (
                  <p className="fw-muted">Adding residents from CounselEAR is switched off right now. Tell the office.</p>
                )}
                {addable && (
                  <>
                    <label className="fw-cepull-confirm">
                      <input type="checkbox" checked={confirmed} disabled={busy} onChange={(e) => setConfirmed(e.target.checked)} />
                      This is the person I am with at {facilityName}.
                    </label>
                    <div className="fw-actions">
                      <Button className="fw-primary" type="button" disabled={!confirmed || busy || offline} onClick={addNow}>
                        {busy ? "Adding…" : "Add to eMerge"}
                      </Button>
                    </div>
                  </>
                )}
              </section>
            )}

            {added && (
              <section className="fw-cepull-done" aria-label="What eMerge did">
                <p role="status">{added.words}</p>
                {added.resident && added.resident.building && added.resident.building.here && added.resident.show && added.resident.patient_id && (
                  <Button className="fw-primary" type="button" onClick={() => showHere(added.resident as CePullResident)}>
                    Show them and open their workbench
                  </Button>
                )}
                {added.resident && !(added.resident.building && added.resident.building.here) && added.resident.ask && (
                  <p className="fw-muted">{added.resident.ask.words}</p>
                )}
                {added.twl_q && <p className="fw-muted">TWL Q from their workbench: {added.twl_q.words}</p>}
              </section>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
